<template>
  <LoginLayout>
    <jet-authentication-card>
      <div class="flex justify-around relative mr-10 ml-10 mt-5">
        <h3 class="text-gray-900 text-l font-medium mt-1">
          Create an attendee account
          <img class="absolute right-0 top-0" src="/image/icons/users-icon.svg" alt="users-icon" />
        </h3>
      </div>

      <jet-validation-errors class="mt-4 px-6" />

      <form @submit.prevent="submit" class="p-6 relative">
        <div>
          <jet-label for="name" value="Name *" />
          <jet-input
            data-cy="user-register-name"
            id="name"
            name="name"
            type="text"
            class="mt-1 block w-full"
            v-model="form.name"
            required
            autofocus
            autocomplete="name"
          />
        </div>

        <div class="mt-4">
          <jet-label for="email" value="Email address *" />
          <jet-input
            data-cy="user-register-email"
            id="email"
            name="email"
            type="email"
            class="mt-1 block w-full"
            v-model="form.email"
            required
            autocomplete="on"
          />
        </div>

        <div class="mt-4">
          <jet-label for="university_id" value="Select organization *" />
          <select
            data-cy="user-register-university"
            id="university_id"
            name="university_id"
            class="
              mt-1
              block
              w-full
              border-gray-300
              focus:border-indigo-300
              focus:ring focus:ring-indigo-200 focus:ring-opacity-50
              rounded-md
              shadow-sm
              sm:text-sm
            "
            v-model="form.university_id"
            @change="loadGroups"
            required
          >
            <!--<option value="" class="small text-green-500">No affiliation</option>-->
            <option value="" class="small text-green-500" disabled>Select a university</option>
            <option v-for="uni in universities" :key="uni.id" :value="uni.id">
              {{ uni.name }}
            </option>
          </select>
        </div>

        <!--<div class="mt-4" v-if="this.form.university_id !== ''">-->
        <!--  <jet-label for="group_id" value="Select group" />-->
        <!--  <select-->
        <!--    data-cy="user-register-group"-->
        <!--    id="group_id"-->
        <!--    name="group_id"-->
        <!--    class="-->
        <!--      mt-1-->
        <!--      block-->
        <!--      w-full-->
        <!--      border-gray-300-->
        <!--      focus:border-indigo-300-->
        <!--      focus:ring focus:ring-indigo-200 focus:ring-opacity-50-->
        <!--      rounded-md-->
        <!--      shadow-sm-->
        <!--      sm:text-sm-->
        <!--    "-->
        <!--    v-model="form.group_id"-->
        <!--  >-->
        <!--    <option value="" disabled>Select a group</option>-->
        <!--    <option v-for="group in groups" :key="group.id" :value="group.id">-->
        <!--      {{ group.name }}-->
        <!--    </option>-->
        <!--  </select>-->
        <!--</div>-->

        <div class="mt-4">
          <jet-label for="password" value="Password" />
          <jet-input
            data-cy="user-register-password"
            id="password"
            name="password"
            :type="fieldType"
            class="mt-1 block w-full"
            v-model="form.password"
            required
            autocomplete="new-password"
            @input="checkPassword"
          />
          <TogglePassword
            @fieldTypeChanged="fieldTypeChanged"
            :password="form.password"
          ></TogglePassword>
        </div>
        <div v-if="passwordValidationFailed" class="mt-1 block text-xxs text-gray-500">
          <span>Password doesn't match the requirements</span>
        </div>
        <div class="mt-4">
          <jet-label for="password_confirmation" value="Confirm password" />
          <jet-input
            data-cy="user-register-confirm-password"
            id="password_confirmation"
            name="password_confirmation"
            :type="confirmationFieldType"
            class="mt-1 block w-full"
            v-model="form.password_confirmation"
            required
            autocomplete="new-password"
          />
          <TogglePassword
            @fieldTypeChanged="confirmationFieldTypeChanged"
            :password="form.password_confirmation"
          ></TogglePassword>
        </div>

        <PasswordChecklist
          class="mt-4"
          :password="form.password"
          @validated="validated"
        ></PasswordChecklist>

        <div class="mt-4" v-if="$page.props.jetstream.hasTermsAndPrivacyPolicyFeature">
          <jet-label for="terms">
            <div class="flex items-center">
              <jet-checkbox name="terms" id="terms" v-model:checked="form.terms" />

              <div class="ml-2">
                I agree to the
                <a
                  target="_blank"
                  :href="route('terms.show')"
                  class="underline text-sm text-gray-600 hover:text-gray-900"
                >
                  Terms of service
                </a>
                and
                <a
                  target="_blank"
                  :href="route('policy.show')"
                  class="underline text-sm text-gray-600 hover:text-gray-900"
                >
                  Privacy Policy
                </a>
              </div>
            </div>
          </jet-label>
        </div>

        <div class="flex items-center justify-end mt-4">
          <inertia-link
            data-cy="user-already-registered"
            :href="route('login')"
            class="font-medium text-vice-teal-700 hover:text-vice-teal-600 text-sm"
            style="color: #3730a3"
          >
            Already registered?
          </inertia-link>

          <jet-button
            data-cy="user-register-confirm"
            class="ml-4"
            :class="{ 'opacity-25': form.processing || passwordValidationFailed }"
            :disabled="form.processing || passwordValidationFailed"
            style="color: #3730a3; background: #e0e7ff; letter-spacing: 0; text-transform: none"
          >
            Register
          </jet-button>
        </div>
      </form>
    </jet-authentication-card>
  </LoginLayout>
</template>

<script>
import JetAuthenticationCard from '@/Jetstream/AuthenticationCard.vue';
import JetButton from '@/Jetstream/Button.vue';
import JetInput from '@/Jetstream/Input.vue';
import JetCheckbox from '@/Jetstream/Checkbox.vue';
import JetLabel from '@/Jetstream/Label.vue';
import JetValidationErrors from '@/Jetstream/ValidationErrors.vue';
import TogglePassword from '@/Components/Badges/TogglePassword.vue';
import PasswordChecklist from '@/Components/PasswordChecklist.vue';
import LoginLayout from '../../Layouts/LoginLayout.vue';

export default {
  components: {
    JetAuthenticationCard,
    JetButton,
    JetInput,
    JetCheckbox,
    JetLabel,
    JetValidationErrors,
    TogglePassword,
    PasswordChecklist,
    LoginLayout,
  },
  data() {
    return {
      form: this.$inertia.form({
        name: '',
        email: '',
        university_id: '',
        group_id: '',
        password: '',
        password_confirmation: '',
        terms: false,
      }),
      fieldType: 'password',
      confirmationFieldType: 'password',
      passwordValidationFailed: false,
      universities: [],
      groups: [],
    };
  },
  methods: {
    submit() {
      this.form.post(this.route('register'), {
        onFinish: () => this.form.reset('password', 'password_confirmation'),
      });
    },
    fieldTypeChanged(e) {
      this.fieldType = e;
    },
    confirmationFieldTypeChanged(e) {
      this.confirmationFieldType = e;
    },
    checkPassword() {
      this.$emit('input', this.password);
    },
    validated(e) {
      this.passwordValidationFailed = e;
    },
    async loadUniversities() {
      try {
        const response = await axios.get('/api/universities');
        this.universities = response.data;
      } catch (error) {
        console.error('Error loading universities:', error);
      }
    },
    async loadGroups() {
      if (this.form.university_id) {
        try {
          const response = await axios.get(`/api/universities/${this.form.university_id}/groups`);
          this.groups = response.data;
          this.form.group_id = '';
        } catch (error) {
          console.error('Error loading groups:', error);
        }
      } else {
        this.groups = [];
        this.form.group_id = '';
      }
    },
  },
  mounted() {
    this.loadUniversities();
  },
};
</script>
